import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2, X, Zap, CreditCard, Loader2, ShieldCheck, FileText,
  Clock, Receipt, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const { user, isPremium, refreshUser } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  // Fetch dynamic pricing with TVA
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/payments/pricing`, { withCredentials: true });
        setPricing(res.data);
      } catch (e) {
        // Fallback pricing (Belgium default)
        setPricing({
          yearly: { htva: 120, vat: 25.20, ttc: 145.20 },
          twoyears: { htva: 200, vat: 42.00, ttc: 242.00 },
          vat_rate: 21, vat_label: 'TVA 21%', country: 'Belgium'
        });
      } finally {
        setLoadingPricing(false);
      }
    };
    if (user) fetchPricing();
    else setLoadingPricing(false);
  }, [user]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setPaymentStatus('timeout'); setPolling(false); return; }
    try {
      const res = await axios.get(`${API}/payments/checkout-status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid'); setPolling(false);
        toast.success('Paiement confirmé ! Votre abonnement Premium est activé.');
        if (refreshUser) refreshUser();
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired'); setPolling(false); return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    }
  }, [refreshUser]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) { setPolling(true); setPaymentStatus('checking'); pollPaymentStatus(sessionId); }
  }, [searchParams, pollPaymentStatus]);

  const handleStripeCheckout = async (planId) => {
    setLoadingPlan(planId);
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, {
        plan_id: planId, origin_url: window.location.origin
      }, { withCredentials: true });
      if (res.data.url) {
        // Open in new tab (works inside iframes) or redirect top window
        const w = window.open(res.data.url, '_blank');
        if (!w) window.top.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de paiement');
    } finally { setLoadingPlan(null); }
  };

  const freeFeatures = [
    { text: `${pricing?.free_jobs_limit ?? 3} publication${(pricing?.free_jobs_limit ?? 3) > 1 ? 's' : ''} de chantier / mois`, ok: true }, { text: 'Candidatures illimitées', ok: true },
    { text: 'Profil basique', ok: true }, { text: 'Carte interactive', ok: true },
    { text: 'Messagerie directe', ok: false }, { text: 'Badge vérifié', ok: false },
    { text: 'Matching IA', ok: false }, { text: 'Analytics avancés', ok: false },
  ];
  const premiumFeatures = [
    'Publications illimitées', 'Candidatures illimitées', 'Profil complet + Portfolio',
    'Carte interactive', 'Messagerie directe', 'Badge vérifié', 'Matching IA avancé',
    'Analytics & Dashboard complet', 'Gestion de projets', 'Devis automatique IA', 'Support prioritaire',
  ];

  const fmt = (n) => n?.toFixed(2).replace('.', ',') + '€';
  const vatRate = pricing?.vat_rate ?? 21;
  const vatLabel = pricing?.vat_label ?? 'TVA 21%';
  const yearlyTTC = pricing?.yearly?.ttc ?? 145.20;
  const twoyearsTTC = pricing?.twoyears?.ttc ?? 242.00;
  const yearlyHTVA = pricing?.yearly?.htva ?? 120;
  const twoyearsHTVA = pricing?.twoyears?.htva ?? 200;
  const yearlyVAT = pricing?.yearly?.vat ?? 25.20;
  const twoyearsVAT = pricing?.twoyears?.vat ?? 42.00;
  const monthlyHTVA = pricing?.monthly?.htva ?? Math.round((yearlyHTVA / 12) * 100) / 100;
  const monthlyTTC = (pricing?.monthly?.ttc ?? yearlyTTC / 12).toFixed(2);
  const discountPct = yearlyHTVA > 0 ? Math.max(0, Math.round((1 - twoyearsHTVA / (2 * yearlyHTVA)) * 100)) : 0;
  const lastSync = pricing?.last_sync_at ? new Date(pricing.last_sync_at) : null;

  if (paymentStatus === 'paid') {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-16" data-testid="payment-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-[#111827] mb-4">Paiement confirmé !</h1>
          <p className="text-[#4B5563] mb-6">Votre abonnement Premium est maintenant actif.</p>
          <div className="bg-[#1E3A8A]/5 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-5 h-5 text-[#1E3A8A]" /><span className="font-semibold text-sm">Facturation</span>
            </div>
            <p className="text-sm text-[#4B5563]">
              Votre facture sera transmise via la plateforme <strong>PEPPOL</strong> (obligatoire en Belgique pour les transactions B2B) et une copie sera envoyée par email à <strong>{user?.email}</strong>.
            </p>
          </div>
          <Button className="btn-primary" onClick={() => window.location.href = '/dashboard'} data-testid="go-dashboard-btn">
            Accéder au dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'timeout' || paymentStatus === 'expired') {
    const expired = paymentStatus === 'expired';
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center py-12">
        <div className="max-w-lg text-center px-4" data-testid="payment-pending-message">
          <Clock className="w-12 h-12 mx-auto mb-4 text-[#F59E0B]" />
          <h1 className="font-heading font-bold text-2xl text-[#111827] mb-2">{expired ? 'Session de paiement expirée' : 'Paiement en cours de confirmation'}</h1>
          <p className="text-[#4B5563]">
            {expired
              ? "La session Stripe a expiré avant la fin du paiement. Aucun montant n'a été débité : vous pouvez relancer le paiement."
              : "Stripe n'a pas encore confirmé votre paiement. Pas d'inquiétude : notre agent vérifie automatiquement les paiements et activera votre abonnement Premium dans un délai maximum de 15 minutes. Vous recevrez la confirmation sur votre tableau de bord."}
          </p>
          <Button className="btn-primary mt-6" onClick={() => { setPaymentStatus(null); window.history.replaceState({}, '', '/dashboard/subscription'); }} data-testid="payment-pending-back">Retour aux abonnements</Button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'checking' || polling) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-16">
        <div className="max-w-md mx-auto px-4 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-[#1E3A8A]" />
          <h1 className="font-heading font-bold text-2xl text-[#111827] mb-2">Vérification du paiement...</h1>
          <p className="text-[#4B5563]">Veuillez patienter, nous vérifions votre paiement auprès de Stripe.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="subscription-title">
            {t('subscription.title')}
          </h1>
          <p className="text-[#4B5563] mt-2 text-lg">Choisissez le plan adapté à votre activité</p>
          {isPremium && (
            <Badge className="bg-[#F59E0B]/10 text-[#92400E] mt-3 text-base px-4 py-1">
              <Zap className="w-4 h-4 mr-1" /> Vous êtes Premium
            </Badge>
          )}
          <div className="mt-5 inline-flex items-start gap-3 text-left bg-[#1E3A8A]/5 border border-[#1E3A8A]/15 rounded-lg px-4 py-3 max-w-2xl" data-testid="subscription-sync-notice">
            <Clock className="w-5 h-5 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[#4B5563]">
              Après paiement, votre passage au Premium est appliqué à votre tableau de bord dans un délai <strong>maximum de {pricing?.sync_interval_minutes ?? 15} minutes</strong> : un agent vérifie automatiquement que votre abonnement et toutes les fonctionnalités sont bien actifs.
              {lastSync && <span className="block text-xs text-[#6B7280] mt-1">Dernière vérification automatique : {lastSync.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan */}
          <Card className={`relative ${!isPremium ? 'border-[#1E3A8A] border-2' : ''}`} data-testid="plan-free">
            {!isPremium && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-[#1E3A8A] text-white">Plan actuel</Badge></div>}
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-xl">Gratuit</CardTitle>
              <div className="mt-4"><span className="text-4xl font-bold text-[#111827]">0€</span><span className="text-[#6B7280] ml-1">/mois</span></div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {freeFeatures.map((f, i) => (
                  <li key={f.text} className="flex items-center gap-2 text-sm">
                    {f.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <X className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                    <span className={f.ok ? 'text-[#374151]' : 'text-[#9CA3AF]'}>{f.text}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <Card className={`relative border-2 ${isPremium ? 'border-[#F59E0B]' : 'border-[#1E3A8A]/20'} bg-gradient-to-b from-[#1E3A8A]/5 to-transparent`} data-testid="plan-premium">
            {isPremium && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-[#F59E0B] text-white">Plan actuel</Badge></div>}
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-xl flex items-center justify-center gap-2">
                <Zap className="w-5 h-5 text-[#F59E0B]" /> Premium
              </CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold text-[#111827]" data-testid="premium-monthly-price">{fmt(monthlyHTVA)}</span>
                <span className="text-[#6B7280] ml-1">/mois HTVA</span>
              </div>
              <p className="text-sm text-[#4B5563] mt-1">
                Soit {monthlyTTC}€/mois TTC
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {premiumFeatures.map((f, i) => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-[#374151]">{f}</span>
                  </li>
                ))}
              </ul>

              {!isPremium && !loadingPricing && (
                <div className="space-y-3">
                  {/* 1 Year Plan */}
                  <Button
                    className="w-full gap-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white h-auto py-3 flex-col"
                    onClick={() => handleStripeCheckout('premium_yearly')}
                    disabled={!!loadingPlan}
                    data-testid="stripe-1year-button"
                  >
                    <div className="flex items-center gap-2">
                      {loadingPlan === 'premium_yearly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      <span className="font-bold">{fmt(yearlyTTC)} TTC pour 1 an</span>
                    </div>
                    <span className="text-xs opacity-80">
                      {fmt(yearlyHTVA)} HTVA + {fmt(yearlyVAT)} {vatLabel}
                    </span>
                  </Button>

                  {/* 2 Year Plan - Limited Launch Offer */}
                  <Button
                    className="w-full gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white h-auto py-3 flex-col relative overflow-hidden"
                    onClick={() => handleStripeCheckout('premium_2years')}
                    disabled={!!loadingPlan}
                    data-testid="stripe-2year-button"
                  >
                    {discountPct > 0 && <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl">OFFRE DE LANCEMENT</div>}
                    <div className="flex items-center gap-2">
                      {loadingPlan === 'premium_2years' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      <span className="font-bold">{fmt(twoyearsTTC)} TTC pour 2 ans</span>
                      {discountPct > 0 && <Badge className="bg-white/20 text-white text-xs">-{discountPct}%</Badge>}
                    </div>
                    <span className="text-xs opacity-80">
                      {fmt(twoyearsHTVA)} HTVA + {fmt(twoyearsVAT)} {vatLabel}
                    </span>
                  </Button>

                  {vatRate > 0 ? (
                    <p className="text-xs text-[#6B7280] text-center">
                      Tous les montants incluent la {vatLabel}
                    </p>
                  ) : (
                    <p className="text-xs text-[#6B7280] text-center">
                      {vatLabel} — TVA en autoliquidation (art. 196 directive 2006/112/CE)
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* PEPPOL + Billing info */}
        <div className="max-w-4xl mx-auto mt-10">
          <Card className="bg-[#1E3A8A]/5 border-[#1E3A8A]/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#1E3A8A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-[#111827] mb-1">Facturation PEPPOL</h3>
                  <p className="text-sm text-[#4B5563]">
                    Conformément à la réglementation belge, toutes les factures sont transmises via la plateforme <strong>PEPPOL</strong> (obligatoire pour les transactions B2B en Belgique). Une copie de votre facture sera également envoyée par email. La facture inclut la TVA applicable selon votre pays.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Security badges */}
        <div className="flex items-center justify-center gap-6 mt-6 text-[#6B7280]">
          <div className="flex items-center gap-1.5 text-xs"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Paiement sécurisé SSL</div>
          <div className="flex items-center gap-1.5 text-xs"><CreditCard className="w-4 h-4 text-[#1E3A8A]" /> Visa, Mastercard, Bancontact</div>
          <div className="flex items-center gap-1.5 text-xs"><Clock className="w-4 h-4 text-[#F59E0B]" /> Activation immédiate</div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mt-12">
          <h2 className="font-heading font-bold text-xl text-[#111827] mb-4 text-center">Questions fréquentes</h2>
          <div className="space-y-3">
            <Card><CardContent className="p-4">
              <h3 className="font-medium text-[#111827] mb-1">Comment payer ?</h3>
              <p className="text-sm text-[#4B5563]">Le paiement se fait par carte bancaire (Visa, Mastercard, Bancontact) via Stripe. Vos données bancaires ne sont jamais stockées sur nos serveurs.</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <h3 className="font-medium text-[#111827] mb-1">Comment reçois-je ma facture ?</h3>
              <p className="text-sm text-[#4B5563]">Votre facture est transmise via la plateforme PEPPOL (obligatoire en Belgique pour les transactions B2B) et une copie est envoyée par email.</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <h3 className="font-medium text-[#111827] mb-1">Quelle TVA est appliquée ?</h3>
              <p className="text-sm text-[#4B5563]">
                <strong>Belgique</strong> : TVA 21% incluse dans le prix. <strong>France et Luxembourg</strong> : TVA 0% — autoliquidation B2B applicable (art. 196 directive 2006/112/CE). La TVA sera clairement mentionnée sur votre facture.
              </p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <h3 className="font-medium text-[#111827] mb-1">Quand mon abonnement sera-t-il activé ?</h3>
              <p className="text-sm text-[#4B5563]">Votre abonnement Premium est activé immédiatement après confirmation du paiement par Stripe.</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <h3 className="font-medium text-[#111827] mb-1">Puis-je annuler ?</h3>
              <p className="text-sm text-[#4B5563]">Votre abonnement reste actif jusqu'à la fin de la période payée (1 an ou 2 ans). Il n'y a pas de renouvellement automatique.</p>
            </CardContent></Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
